import { useMutation, useQuery } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import type { Json } from '@/types/database.generated';

// ---- Types ----

export type FormsAssignmentStatus =
  | 'sent'
  | 'reminded'
  | 'completed'
  | 'expired'
  | 'cancelled';

export interface PatientFormsAssignment {
  id: string;
  clinic_id: string;
  patient_id: string;
  forms_assignment_id: string;
  forms_template_id: string;
  template_summary: {
    name?: string;
    locale?: string;
    version?: string;
    scoring_method?: string;
    total_categories?: number;
    last_updated_at?: string;
  };
  status: FormsAssignmentStatus;
  sent_at: string | null;
  completed_at: string | null;
  expired_at: string | null;
  due_at: string | null;
  score: Json | null;
  metadata: Json | null;
  created_at: string;
  updated_at: string;
}

export interface RequestPortalLinkResult {
  ok: boolean;
  url?: string;
  error?: string;
}

// ---- Query keys ----

export const patientFormsAssignmentsKey = (patientId: string | null | undefined) =>
  ['portal', 'forms-assignments', patientId ?? null] as const;

// ---- Queries ----

/**
 * Lists patient_forms_assignments for the given patient (RLS enforced).
 * Used by the patient-portal questionnaires page (FORMS-5).
 */
export function usePatientFormsAssignments(patientId: string | null | undefined) {
  return useQuery({
    queryKey: patientFormsAssignmentsKey(patientId),
    enabled: Boolean(patientId),
    queryFn: async (): Promise<PatientFormsAssignment[]> => {
      if (!patientId) return [];

      const { data, error } = await supabase
        .from('patient_forms_assignments')
        .select(
          'id, clinic_id, patient_id, forms_assignment_id, forms_template_id, template_summary, status, sent_at, completed_at, expired_at, due_at, score, metadata, created_at, updated_at',
        )
        .eq('patient_id', patientId)
        .order('created_at', { ascending: false });

      if (error) throw error;
      return (data ?? []) as PatientFormsAssignment[];
    },
    staleTime: 30_000,
  });
}

// ---- Mutations ----

/**
 * Requests the public fill-out URL for a portal patient's questionnaire assignment.
 */
export function useRequestPortalFormLink() {
  return useMutation({
    mutationFn: async (assignmentId: string): Promise<RequestPortalLinkResult> => {
      const { data, error } = await supabase.functions.invoke<{
        ok: boolean;
        data?: { url: string };
        error?: { message?: string };
      }>('forms-portal-form-link', {
        body: { assignment_id: assignmentId },
      });

      if (error) throw error;
      if (data?.ok && data.data?.url) {
        return { ok: true, url: data.data.url };
      }

      return {
        ok: false,
        error: data?.error?.message ?? 'Could not load questionnaire link',
      };
    },
  });
}
